import { fetchPenaltyLevels, fetchSpeedrunLevels, searchUsersByName } from "../lib/cloud";
import {
  createAdminChallengeLobbyTip,
  createAdminChallengeTemplate,
  deleteAdminChallengeLobbyTip,
  deleteAdminChallengeTemplate,
  fetchAdminChallenges,
  resetAdminDailyChallenges,
  saveAdminChallengeRewardWeights,
  updateAdminChallengeLobbyTip,
  updateAdminChallengeTemplate,
  type AdminChallengeLobbyTip,
  type AdminChallengeRewardWeight,
  type AdminChallengeTemplate,
} from "./challenges/api";
import {
  advanceFlipCountdown,
  createFlipCountdown,
  drawFlipCountdown,
  syncFlipCountdown,
} from "./challenges/flip-countdown";
import { drawChallengeLobbyTipText } from "./challenges/lobby-tips-draw";
import {
  CHALLENGE_MODE_OPTIONS,
  CHALLENGE_REWARD_AMOUNTS,
  challengeModeNeedsLevel,
} from "./challenges/types";

const PROB_STEP = 1; // percent
const PROB_STEP_BP = 100; // 1% in basis points (total = 10_000)

function bpToPercent(bp: number) {
  return bp / 100;
}

function formatPercent(bp: number) {
  return `${bpToPercent(bp).toFixed(2)}%`;
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function fixBasisPointsTotal(bp: number[]) {
  const total = bp.reduce((sum, value) => sum + value, 0);
  if (!bp.length || total === 10_000) return;
  const diff = 10_000 - total;
  const maxIdx = bp.reduce((best, value, index, arr) => (value > arr[best] ? index : best), 0);
  bp[maxIdx] = Math.max(0, bp[maxIdx] + diff);
}

/** Split `amount` across `indices` evenly (largest-remainder). `sign` +1 adds, -1 subtracts (clamped at 0). */
function distributeBasisPoints(bp: number[], indices: number[], amount: number, sign: 1 | -1) {
  if (!indices.length || amount <= 0) return;
  const base = Math.floor(amount / indices.length);
  let extra = amount - base * indices.length;
  for (const index of indices) {
    const delta = base + (extra > 0 ? 1 : 0);
    if (extra > 0) extra -= 1;
    if (sign === 1) bp[index] += delta;
    else bp[index] = Math.max(0, bp[index] - delta);
  }
}

/** After an equal subtract, recover any shortfall from rows that still have weight. */
function reclaimBasisPoints(bp: number[], indices: number[], need: number) {
  let left = need;
  while (left > 0) {
    const live = indices.filter((i) => bp[i] > 0);
    if (!live.length) break;
    const share = Math.max(1, Math.floor(left / live.length));
    for (const i of live) {
      if (left <= 0) break;
      const take = Math.min(bp[i], share, left);
      bp[i] -= take;
      left -= take;
    }
  }
  return need - left;
}

export function mountAdminChallenges(root: HTMLElement) {
  root.replaceChildren();

  const toolbar = document.createElement("div");
  toolbar.className = "modeBox adminRecordsFilters";
  const addBtn = document.createElement("button");
  addBtn.type = "button";
  addBtn.className = "btn green";
  addBtn.textContent = "Add Template";
  const resetDailyBtn = document.createElement("button");
  resetDailyBtn.type = "button";
  resetDailyBtn.className = "btn";
  resetDailyBtn.textContent = "Reset Challenge";
  resetDailyBtn.title = "Clear today's Challenge attempt for all players or selected users";
  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.className = "btn";
  refreshBtn.textContent = "Refresh";
  toolbar.append(addBtn, resetDailyBtn, refreshBtn);
  root.appendChild(toolbar);

  const status = document.createElement("p");
  status.className = "accessHint";
  root.appendChild(status);

  const resetPanel = document.createElement("div");
  resetPanel.className = "modeBox adminAchievementEditor hidden adminChallengeResetPanel";
  root.appendChild(resetPanel);

  const editor = document.createElement("div");
  editor.className = "modeBox adminAchievementEditor hidden";
  root.appendChild(editor);

  const listWrap = document.createElement("div");
  listWrap.className = "leaderboardWrap";
  root.appendChild(listWrap);

  const rewardsHeading = document.createElement("h4");
  rewardsHeading.className = "adminPlayerStatsSubheading";
  rewardsHeading.textContent = "Dots reward probabilities";
  root.appendChild(rewardsHeading);

  const rewardsHint = document.createElement("p");
  rewardsHint.className = "accessHint";
  rewardsHint.textContent =
    "Weights for the success roulette (500–10,000 Dots). Total must equal 100% (same rule as Crates). For Progression templates, Score means Level (runs start at Level 1).";
  root.appendChild(rewardsHint);

  const rewardsWrap = document.createElement("div");
  rewardsWrap.className = "leaderboardWrap";
  root.appendChild(rewardsWrap);

  const saveRewardsBtn = document.createElement("button");
  saveRewardsBtn.type = "button";
  saveRewardsBtn.className = "btn green";
  saveRewardsBtn.textContent = "Save reward probabilities";
  root.appendChild(saveRewardsBtn);

  const tipsHeading = document.createElement("h4");
  tipsHeading.className = "adminPlayerStatsSubheading";
  tipsHeading.textContent = "Lobby idle texts";
  root.appendChild(tipsHeading);

  const tipsHint = document.createElement("p");
  tipsHint.className = "accessHint";
  tipsHint.textContent =
    "Texts that appear after the player stays in the Challenges lobby. Placement is anchored to the square so it stays identical on every screen. Choose blue, red, or both; optionally freeze or fade out the red countdown.";
  root.appendChild(tipsHint);

  const tipToolbar = document.createElement("div");
  tipToolbar.className = "modeBox adminRecordsFilters";
  const addTipBtn = document.createElement("button");
  addTipBtn.type = "button";
  addTipBtn.className = "btn green";
  addTipBtn.textContent = "Add idle text";
  tipToolbar.appendChild(addTipBtn);
  root.appendChild(tipToolbar);

  const tipEditor = document.createElement("div");
  tipEditor.className = "modeBox adminAchievementEditor hidden adminChallengeTipEditor";
  root.appendChild(tipEditor);

  const tipListWrap = document.createElement("div");
  tipListWrap.className = "leaderboardWrap";
  root.appendChild(tipListWrap);

  let editingId: string | null = null;
  let rewardRows: AdminChallengeRewardWeight[] = [];
  let tipRows: AdminChallengeLobbyTip[] = [];
  let editingTipId: string | null = null;
  let tipDraft: {
    text: string;
    posX: number;
    posY: number;
    squareState: "blue" | "red" | "both";
    holdSeconds: number;
    durationSeconds: number;
    pauseCountdown: boolean;
    hideCountdown: boolean;
    active: boolean;
  } = {
    text: "Stay here a little longer…",
    posX: 0.5,
    posY: -0.35,
    squareState: "both",
    holdSeconds: 5,
    durationSeconds: 4,
    pauseCountdown: false,
    hideCountdown: false,
    active: true,
  };
  let tipPreviewRaf = 0;
  let tipPreviewFlip = createFlipCountdown("00:00:00:00");
  let tipPreviewEndsAt = Date.now() + 36e5;
  let tipPreviewPausedAccum = 0;
  let tipPreviewPauseStarted = 0;
  let tipPreviewAnimationStarted = performance.now();
  let tipDragging = false;

  // Fresh controls each open — reusing detached <select> nodes after replaceChildren
  // leaves empty dropdowns on the second Add Template.
  let modeSelect: HTMLSelectElement;
  let levelSelect: HTMLSelectElement;
  let levelRow: HTMLDivElement;
  let metricSelect: HTMLSelectElement;
  let metricRow: HTMLDivElement;
  let goalLabel: HTMLSpanElement;
  let goalInput: HTMLInputElement;
  let activeCheck: HTMLInputElement;
  let activeLabel: HTMLLabelElement;
  let saveBtn: HTMLButtonElement;
  let cancelBtn: HTMLButtonElement;
  let speedrunLevelOptions: Array<{ levelIndex: number; label: string }> = [];
  let golfLevelOptions: Array<{ levelIndex: number; label: string }> = [];

  async function ensureLevelOptionsLoaded() {
    if (!speedrunLevelOptions.length) {
      try {
        const levels = await fetchSpeedrunLevels();
        speedrunLevelOptions = levels
          .map((l) => ({
            levelIndex: l.levelIndex,
            label: l.displayName?.trim()
              ? `${l.levelIndex} — ${l.displayName.trim()}`
              : `Level ${l.levelIndex}`,
          }))
          .sort((a, b) => a.levelIndex - b.levelIndex);
      } catch {
        speedrunLevelOptions = [];
      }
    }
    if (!golfLevelOptions.length) {
      try {
        const levels = await fetchPenaltyLevels();
        golfLevelOptions = levels
          .map((l) => ({
            levelIndex: l.levelIndex,
            label: l.displayName?.trim()
              ? `${l.levelIndex} — ${l.displayName.trim()}`
              : `Level ${l.levelIndex}`,
          }))
          .sort((a, b) => a.levelIndex - b.levelIndex);
      } catch {
        golfLevelOptions = [];
      }
    }
  }

  function syncLevelSelectOptions(preferred?: number | null) {
    const modeId = modeSelect.value;
    const needsLevel = challengeModeNeedsLevel(modeId);
    levelRow.classList.toggle("hidden", !needsLevel);
    levelSelect.replaceChildren();
    if (!needsLevel) {
      levelSelect.value = "";
      return;
    }
    const opts = modeId === "penalty" ? golfLevelOptions : speedrunLevelOptions;
    if (!opts.length) {
      const empty = document.createElement("option");
      empty.value = "";
      empty.textContent = modeId === "penalty" ? "No Golf levels published" : "No SpeedRun levels published";
      levelSelect.appendChild(empty);
      levelSelect.value = "";
      return;
    }
    for (const o of opts) {
      const opt = document.createElement("option");
      opt.value = String(o.levelIndex);
      opt.textContent = o.label;
      levelSelect.appendChild(opt);
    }
    const pref = Math.floor(Number(preferred) || 0);
    if (pref >= 1 && opts.some((o) => o.levelIndex === pref)) {
      levelSelect.value = String(pref);
    } else {
      levelSelect.value = String(opts[0]!.levelIndex);
    }
  }

  function createEditorControls() {
    modeSelect = document.createElement("select");
    modeSelect.className = "formInput";
    for (const m of CHALLENGE_MODE_OPTIONS) {
      const opt = document.createElement("option");
      opt.value = m.id;
      opt.textContent = m.label;
      modeSelect.appendChild(opt);
    }

    levelSelect = document.createElement("select");
    levelSelect.className = "formInput";
    levelSelect.setAttribute("aria-label", "Level");

    levelRow = document.createElement("div");
    levelRow.className = "formRow hidden";
    const levelLabel = document.createElement("span");
    levelLabel.className = "formLabel";
    levelLabel.textContent = "Level";
    levelRow.append(levelLabel, levelSelect);

    metricSelect = document.createElement("select");
    metricSelect.className = "formInput";
    for (const m of [
      { id: "score", label: "Score (Level for Progression)" },
      { id: "time", label: "Time (seconds)" },
    ]) {
      const opt = document.createElement("option");
      opt.value = m.id;
      opt.textContent = m.label;
      metricSelect.appendChild(opt);
    }

    goalInput = document.createElement("input");
    goalInput.className = "formInput";
    goalInput.type = "number";
    goalInput.min = "1";
    goalInput.placeholder = "Goal value";

    activeCheck = document.createElement("input");
    activeCheck.type = "checkbox";
    activeCheck.checked = true;
    activeLabel = document.createElement("label");
    activeLabel.className = "formLabel";
    activeLabel.append(activeCheck, document.createTextNode(" Active"));

    saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "btn green";
    saveBtn.textContent = "Save";
    cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";

    modeSelect.onchange = () => {
      syncModeDependentFields(Number(levelSelect.value) || null);
    };

    saveBtn.onclick = async () => {
      const modeId = modeSelect.value;
      const needsLevel = challengeModeNeedsLevel(modeId);
      const levelIndex = Math.floor(Number(levelSelect.value) || 0);
      if (needsLevel && levelIndex < 1) {
        status.textContent =
          modeId === "penalty"
            ? "Select a Golf level (publish one in Admin → Golf Levels first)."
            : "Select a SpeedRun level (publish one in Admin → SpeedRun Levels first).";
        return;
      }
      const goalValue = Math.floor(Number(goalInput.value) || 0);
      if (goalValue < 1) {
        status.textContent = needsLevel ? "Time limit must be ≥ 1 second" : "Goal must be ≥ 1";
        return;
      }
      try {
        const payload = {
          modeId,
          metric: needsLevel ? "time" : metricSelect.value,
          goalValue,
          levelIndex: needsLevel ? levelIndex : null,
          active: activeCheck.checked,
        };
        if (editingId) {
          await updateAdminChallengeTemplate({ id: editingId, ...payload });
        } else {
          await createAdminChallengeTemplate(payload);
        }
        closeEditor();
        await refresh();
      } catch (e) {
        status.textContent = e instanceof Error ? e.message : "Save failed";
      }
    };
    cancelBtn.onclick = () => closeEditor();
  }

  function syncModeDependentFields(prefLevel: number | null) {
    const needsLevel = challengeModeNeedsLevel(modeSelect.value);
    syncLevelSelectOptions(prefLevel);
    if (needsLevel) {
      metricSelect.value = "time";
      metricSelect.disabled = true;
      metricRow.classList.add("hidden");
      goalLabel.textContent = "Time limit (s)";
      goalInput.placeholder = "Seconds to finish";
      if (!goalInput.value || Number(goalInput.value) < 1) goalInput.value = "30";
    } else {
      metricSelect.disabled = false;
      metricRow.classList.remove("hidden");
      goalLabel.textContent = "Goal";
      goalInput.placeholder = "Goal value";
    }
  }

  function buildEditor() {
    createEditorControls();
    editor.replaceChildren();
    const title = document.createElement("h4");
    title.className = "adminPlayerStatsSubheading";
    title.textContent = editingId ? "Edit template" : "New template";
    const row1 = document.createElement("div");
    row1.className = "formRow";
    const l1 = document.createElement("span");
    l1.className = "formLabel";
    l1.textContent = "Mode";
    row1.append(l1, modeSelect);
    const row2 = document.createElement("div");
    row2.className = "formRow";
    metricRow = row2;
    const l2 = document.createElement("span");
    l2.className = "formLabel";
    l2.textContent = "Metric";
    row2.append(l2, metricSelect);
    const row3 = document.createElement("div");
    row3.className = "formRow";
    goalLabel = document.createElement("span");
    goalLabel.className = "formLabel";
    goalLabel.textContent = "Goal";
    row3.append(goalLabel, goalInput);
    const actions = document.createElement("div");
    actions.className = "formRow";
    actions.append(activeLabel, saveBtn, cancelBtn);
    editor.append(title, row1, levelRow, row2, row3, actions);
  }

  async function openEditor(row?: AdminChallengeTemplate) {
    editingId = row?.id ?? null;
    await ensureLevelOptionsLoaded();
    buildEditor();
    modeSelect.value = row?.modeId ?? "normal";
    metricSelect.value = row?.metric === "time" ? "time" : "score";
    goalInput.value = String(
      row?.goalValue ?? (challengeModeNeedsLevel(modeSelect.value) ? 30 : 100),
    );
    activeCheck.checked = row ? !!row.active : true;
    syncModeDependentFields(row?.levelIndex ?? null);
    editor.classList.remove("hidden");
  }

  function closeEditor() {
    editingId = null;
    editor.classList.add("hidden");
    editor.replaceChildren();
  }

  function renderList(templates: AdminChallengeTemplate[]) {
    listWrap.replaceChildren();
    if (!templates.length) {
      const p = document.createElement("p");
      p.className = "accessHint";
      p.textContent = "No templates yet. Add at least one active template for the mode roulette.";
      listWrap.appendChild(p);
      return;
    }
    const table = document.createElement("table");
    table.className = "leaderboardTable";
    table.innerHTML =
      "<thead><tr><th>Mode</th><th>Level</th><th>Metric</th><th>Goal</th><th>Active</th><th></th></tr></thead>";
    const tbody = document.createElement("tbody");
    for (const t of templates) {
      const tr = document.createElement("tr");
      const levelCell = challengeModeNeedsLevel(t.modeId)
        ? t.levelIndex != null && t.levelIndex >= 1
          ? String(t.levelIndex)
          : "—"
        : "—";
      const metricCell = challengeModeNeedsLevel(t.modeId) ? "time limit" : t.metric;
      const goalCell = challengeModeNeedsLevel(t.modeId) ? `${t.goalValue}s` : String(t.goalValue);
      tr.innerHTML = `<td>${t.modeLabel}</td><td>${levelCell}</td><td>${metricCell}</td><td>${goalCell}</td><td>${t.active ? "Yes" : "No"}</td>`;
      const td = document.createElement("td");
      const edit = document.createElement("button");
      edit.type = "button";
      edit.className = "btn";
      edit.textContent = "Edit";
      edit.onclick = () => void openEditor(t);
      const del = document.createElement("button");
      del.type = "button";
      del.className = "btn";
      del.textContent = "Delete";
      del.onclick = async () => {
        if (!confirm(`Delete template ${t.modeLabel}?`)) return;
        try {
          await deleteAdminChallengeTemplate(t.id);
          await refresh();
        } catch (e) {
          status.textContent = e instanceof Error ? e.message : "Delete failed";
        }
      };
      td.append(edit, del);
      tr.appendChild(td);
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    listWrap.appendChild(table);
  }

  function adjustReward(index: number, deltaPercent: number) {
    if (!deltaPercent || rewardRows.length < 2) return;
    const bp = rewardRows.map((r) => Math.max(0, Math.floor(Number(r.probabilityBasisPoints) || 0)));
    const total = bp.reduce((sum, value) => sum + value, 0);
    if (total <= 0) {
      const base = Math.floor(10_000 / bp.length);
      let rem = 10_000 - base * bp.length;
      for (let i = 0; i < bp.length; i++) {
        bp[i] = base + (rem > 0 ? 1 : 0);
        if (rem > 0) rem -= 1;
      }
    } else if (total !== 10_000) {
      const normalized = bp.map((value) => Math.round((value * 10_000) / total));
      fixBasisPointsTotal(normalized);
      for (let i = 0; i < bp.length; i++) bp[i] = normalized[i]!;
    }

    const others = bp.map((_, i) => i).filter((i) => i !== index);
    if (deltaPercent > 0) {
      const othersTotal = others.reduce((sum, i) => sum + bp[i]!, 0);
      if (othersTotal < PROB_STEP_BP) return;
      // Take evenly from every other prize, then reclaim if clamps left a shortfall.
      const before = others.reduce((sum, i) => sum + bp[i]!, 0);
      distributeBasisPoints(bp, others, PROB_STEP_BP, -1);
      const taken = before - others.reduce((sum, i) => sum + bp[i]!, 0);
      const short = PROB_STEP_BP - taken;
      if (short > 0) reclaimBasisPoints(bp, others, short);
      const got =
        before - others.reduce((sum, i) => sum + bp[i]!, 0);
      if (got <= 0) return;
      bp[index] = (bp[index] || 0) + got;
    } else {
      if ((bp[index] || 0) < PROB_STEP_BP) return;
      bp[index]! -= PROB_STEP_BP;
      // Give evenly to every other prize.
      distributeBasisPoints(bp, others, PROB_STEP_BP, 1);
    }
    fixBasisPointsTotal(bp);
    rewardRows = rewardRows.map((r, i) => ({
      ...r,
      probabilityBasisPoints: bp[i]!,
      probabilityPercent: bpToPercent(bp[i]!),
    }));
    renderRewards();
  }

  function renderRewards() {
    rewardsWrap.replaceChildren();
    const table = document.createElement("table");
    table.className = "leaderboardTable";
    table.innerHTML = "<thead><tr><th>Dots</th><th>Chance</th><th></th></tr></thead>";
    const tbody = document.createElement("tbody");
    rewardRows.forEach((row, idx) => {
      const tr = document.createElement("tr");
      const tdA = document.createElement("td");
      tdA.textContent = String(row.amountDots);
      const tdP = document.createElement("td");
      tdP.textContent = formatPercent(row.probabilityBasisPoints);
      const tdB = document.createElement("td");
      const btns = document.createElement("div");
      btns.className = "adminCrateProbBtns";
      const up = document.createElement("button");
      up.type = "button";
      up.className = "btn adminCrateProbBtn";
      up.textContent = "▲";
      up.onclick = () => adjustReward(idx, PROB_STEP);
      const down = document.createElement("button");
      down.type = "button";
      down.className = "btn adminCrateProbBtn";
      down.textContent = "▼";
      down.onclick = () => adjustReward(idx, -PROB_STEP);
      btns.append(up, down);
      tdB.appendChild(btns);
      tr.append(tdA, tdP, tdB);
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    rewardsWrap.appendChild(table);
    const sum = rewardRows.reduce((s, r) => s + r.probabilityBasisPoints, 0);
    const sumLine = document.createElement("p");
    sumLine.className = "accessHint";
    sumLine.textContent = `Total: ${formatPercent(sum)}${sum === 10000 ? "" : " — must be 100%"}`;
    rewardsWrap.appendChild(sumLine);
  }

  async function refresh() {
    status.textContent = "Loading…";
    try {
      const data = await fetchAdminChallenges();
      renderList(data.templates || []);
      rewardRows = (data.rewardWeights || []).length
        ? data.rewardWeights
        : CHALLENGE_REWARD_AMOUNTS.map((amountDots) => ({
            amountDots,
            probabilityBasisPoints: 0,
            probabilityPercent: 0,
          }));
      renderRewards();
      tipRows = data.lobbyTips || [];
      renderTipList();
      status.textContent = "";
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Failed to load";
    }
  }

  function tipPreviewSquareState(): "blue" | "red" {
    if (tipDraft.squareState === "red") return "red";
    if (tipDraft.squareState === "blue") return "blue";
    return tipDraft.squareState === "both" && tipEditor.dataset.previewToggle === "red"
      ? "red"
      : "blue";
  }

  function tipPreviewRemainingMs() {
    let rem = tipPreviewEndsAt - Date.now() + tipPreviewPausedAccum;
    if (tipPreviewPauseStarted > 0) rem += Date.now() - tipPreviewPauseStarted;
    return Math.max(0, rem);
  }

  function formatTipCountdown(ms: number) {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(days)}:${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  }

  function stopTipPreviewLoop() {
    if (tipPreviewRaf) cancelAnimationFrame(tipPreviewRaf);
    tipPreviewRaf = 0;
  }

  function paintTipPreview(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "#07090c";
    ctx.fillRect(0, 0, w, h);

    const sqSize = Math.min(w, h) * 0.42;
    const sq = { x: (w - sqSize) / 2, y: (h - sqSize) / 2, size: sqSize };
    const blocked = tipPreviewSquareState() === "red";
    const color = blocked ? "#c44a55" : "#b8ffff";
    const glow = blocked ? "#a03842" : "#7efcff";
    const duration = Math.max(1, tipDraft.durationSeconds);
    const elapsed =
      ((performance.now() - tipPreviewAnimationStarted) / 1000) % (duration + 1);
    const tipAlpha =
      elapsed > duration
        ? 0
        : Math.min(1, elapsed / 0.25, Math.max(0, (duration - elapsed) / 0.3));
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = blocked ? 2.5 : 4;
    ctx.shadowColor = glow;
    ctx.shadowBlur = blocked ? 10 : 28;
    ctx.strokeRect(sq.x, sq.y, sq.size, sq.size);
    ctx.restore();

    if (blocked) {
      if (tipDraft.pauseCountdown) {
        if (tipPreviewPauseStarted <= 0) tipPreviewPauseStarted = Date.now();
      } else if (tipPreviewPauseStarted > 0) {
        tipPreviewPausedAccum += Date.now() - tipPreviewPauseStarted;
        tipPreviewPauseStarted = 0;
      }
      const rem = tipPreviewRemainingMs();
      const label = formatTipCountdown(rem);
      syncFlipCountdown(tipPreviewFlip, label);
      advanceFlipCountdown(tipPreviewFlip, 1 / 60);
      const fontSize = Math.max(10, Math.floor(sq.size * 0.11));
      drawFlipCountdown(
        ctx,
        tipPreviewFlip,
        sq.x + sq.size / 2,
        sq.y + sq.size / 2,
        fontSize,
        "rgba(255, 170, 178, 0.92)",
        sq.size * 0.88,
        tipDraft.hideCountdown ? 1 - tipAlpha : 1,
      );
    } else if (tipPreviewPauseStarted > 0) {
      tipPreviewPausedAccum += Date.now() - tipPreviewPauseStarted;
      tipPreviewPauseStarted = 0;
    }

    drawChallengeLobbyTipText(ctx, tipDraft, {
      square: sq,
      alpha: tipAlpha,
      color: blocked ? "rgba(255, 170, 178, 0.92)" : "#ffffff",
      shadowColor: blocked ? "#a03842" : "rgba(180, 230, 255, 0.85)",
    });
  }

  function startTipPreviewLoop(canvas: HTMLCanvasElement) {
    stopTipPreviewLoop();
    const tick = () => {
      paintTipPreview(canvas);
      tipPreviewRaf = requestAnimationFrame(tick);
    };
    tipPreviewRaf = requestAnimationFrame(tick);
  }

  function buildTipEditor() {
    stopTipPreviewLoop();
    tipEditor.replaceChildren();
    const title = document.createElement("h4");
    title.className = "adminPlayerStatsSubheading";
    title.textContent = editingTipId ? "Edit idle text" : "New idle text";

    const textInput = document.createElement("textarea");
    textInput.className = "formInput adminChallengeTipText";
    textInput.rows = 2;
    textInput.maxLength = 280;
    textInput.value = tipDraft.text;
    textInput.placeholder = "Text shown in the lobby";

    const holdInput = document.createElement("input");
    holdInput.className = "formInput";
    holdInput.type = "number";
    holdInput.min = "0";
    holdInput.max = "3600";
    holdInput.value = String(tipDraft.holdSeconds);
    const durInput = document.createElement("input");
    durInput.className = "formInput";
    durInput.type = "number";
    durInput.min = "1";
    durInput.max = "120";
    durInput.value = String(tipDraft.durationSeconds);

    const stateSelect = document.createElement("select");
    stateSelect.className = "formInput";
    for (const opt of [
      { id: "both", label: "Blue or red" },
      { id: "blue", label: "Only blue (available)" },
      { id: "red", label: "Only red (locked)" },
    ]) {
      const o = document.createElement("option");
      o.value = opt.id;
      o.textContent = opt.label;
      stateSelect.appendChild(o);
    }
    stateSelect.value = tipDraft.squareState;

    const pauseCheck = document.createElement("input");
    pauseCheck.type = "checkbox";
    pauseCheck.checked = tipDraft.pauseCountdown;
    const pauseLabel = document.createElement("label");
    pauseLabel.className = "formLabel";
    pauseLabel.append(pauseCheck, document.createTextNode(" Pause red countdown while text shows"));

    const hideCountdownCheck = document.createElement("input");
    hideCountdownCheck.type = "checkbox";
    hideCountdownCheck.checked = tipDraft.hideCountdown;
    const hideCountdownLabel = document.createElement("label");
    hideCountdownLabel.className = "formLabel";
    hideCountdownLabel.append(
      hideCountdownCheck,
      document.createTextNode(" Fade out red countdown while text shows"),
    );

    const activeCheckTip = document.createElement("input");
    activeCheckTip.type = "checkbox";
    activeCheckTip.checked = tipDraft.active;
    const activeLabelTip = document.createElement("label");
    activeLabelTip.className = "formLabel";
    activeLabelTip.append(activeCheckTip, document.createTextNode(" Active"));

    const previewWrap = document.createElement("div");
    previewWrap.className = "adminChallengeTipPreviewWrap";
    const previewLabel = document.createElement("span");
    previewLabel.className = "formLabel";
    previewLabel.textContent = "Preview — drag the text to move it";
    const canvas = document.createElement("canvas");
    canvas.className = "adminChallengeTipPreview";
    canvas.width = 420;
    canvas.height = 280;
    canvas.title = "Drag to place the text";

    const toggleBtn = document.createElement("button");
    toggleBtn.type = "button";
    toggleBtn.className = "btn";
    tipEditor.dataset.previewToggle = tipDraft.squareState === "red" ? "red" : "blue";
    const syncToggleLabel = () => {
      const shown = tipPreviewSquareState();
      toggleBtn.textContent =
        tipDraft.squareState === "both"
          ? `Preview square: ${shown === "red" ? "Red" : "Blue"} (click to toggle)`
          : `Preview square: ${shown === "red" ? "Red" : "Blue"}`;
      toggleBtn.disabled = tipDraft.squareState !== "both";
    };
    syncToggleLabel();
    toggleBtn.onclick = () => {
      if (tipDraft.squareState !== "both") return;
      tipEditor.dataset.previewToggle =
        tipEditor.dataset.previewToggle === "red" ? "blue" : "red";
      syncToggleLabel();
    };

    const syncDraftFromInputs = () => {
      tipDraft.text = textInput.value;
      tipDraft.holdSeconds = Math.max(0, Math.floor(Number(holdInput.value) || 0));
      tipDraft.durationSeconds = Math.max(1, Math.floor(Number(durInput.value) || 1));
      tipDraft.squareState = stateSelect.value as "blue" | "red" | "both";
      tipDraft.pauseCountdown = pauseCheck.checked;
      tipDraft.hideCountdown = hideCountdownCheck.checked;
      tipDraft.active = activeCheckTip.checked;
      syncToggleLabel();
    };
    textInput.addEventListener("input", syncDraftFromInputs);
    holdInput.addEventListener("input", syncDraftFromInputs);
    durInput.addEventListener("input", syncDraftFromInputs);
    stateSelect.addEventListener("change", syncDraftFromInputs);
    pauseCheck.addEventListener("change", syncDraftFromInputs);
    hideCountdownCheck.addEventListener("change", syncDraftFromInputs);
    activeCheckTip.addEventListener("change", syncDraftFromInputs);

    const onPointer = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const px = (clientX - rect.left) * scaleX;
      const py = (clientY - rect.top) * scaleY;
      const sqSize = Math.min(canvas.width, canvas.height) * 0.42;
      const sqX = (canvas.width - sqSize) / 2;
      const sqY = (canvas.height - sqSize) / 2;
      tipDraft.posX = Math.min(4, Math.max(-3, (px - sqX) / sqSize));
      tipDraft.posY = Math.min(4, Math.max(-3, (py - sqY) / sqSize));
    };
    canvas.addEventListener("pointerdown", (e) => {
      tipDragging = true;
      canvas.setPointerCapture?.(e.pointerId);
      onPointer(e.clientX, e.clientY);
    });
    canvas.addEventListener("pointermove", (e) => {
      if (!tipDragging) return;
      onPointer(e.clientX, e.clientY);
    });
    canvas.addEventListener("pointerup", () => {
      tipDragging = false;
    });
    canvas.addEventListener("pointercancel", () => {
      tipDragging = false;
    });

    tipPreviewEndsAt = Date.now() + 5 * 3600 * 1000;
    tipPreviewPausedAccum = 0;
    tipPreviewPauseStarted = 0;
    tipPreviewAnimationStarted = performance.now();
    tipPreviewFlip = createFlipCountdown("00:05:00:00");
    previewWrap.append(previewLabel, canvas, toggleBtn);

    const rowHold = document.createElement("div");
    rowHold.className = "formRow";
    const lHold = document.createElement("span");
    lHold.className = "formLabel";
    lHold.textContent = "Appear after (seconds in lobby)";
    rowHold.append(lHold, holdInput);

    const rowDur = document.createElement("div");
    rowDur.className = "formRow";
    const lDur = document.createElement("span");
    lDur.className = "formLabel";
    lDur.textContent = "Text duration (seconds)";
    rowDur.append(lDur, durInput);

    const rowState = document.createElement("div");
    rowState.className = "formRow";
    const lState = document.createElement("span");
    lState.className = "formLabel";
    lState.textContent = "Show when square is";
    rowState.append(lState, stateSelect);

    const saveTipBtn = document.createElement("button");
    saveTipBtn.type = "button";
    saveTipBtn.className = "btn green";
    saveTipBtn.textContent = "Save";
    const cancelTipBtn = document.createElement("button");
    cancelTipBtn.type = "button";
    cancelTipBtn.className = "btn";
    cancelTipBtn.textContent = "Cancel";
    const actions = document.createElement("div");
    actions.className = "formRow";
    actions.append(
      activeLabelTip,
      pauseLabel,
      hideCountdownLabel,
      saveTipBtn,
      cancelTipBtn,
    );

    saveTipBtn.onclick = async () => {
      syncDraftFromInputs();
      if (!tipDraft.text.trim()) {
        status.textContent = "Idle text cannot be empty";
        return;
      }
      try {
        if (editingTipId) {
          await updateAdminChallengeLobbyTip({ id: editingTipId, ...tipDraft });
        } else {
          await createAdminChallengeLobbyTip(tipDraft);
        }
        closeTipEditor();
        await refresh();
      } catch (e) {
        status.textContent = e instanceof Error ? e.message : "Save tip failed";
      }
    };
    cancelTipBtn.onclick = () => closeTipEditor();

    tipEditor.append(
      title,
      textInput,
      rowHold,
      rowDur,
      rowState,
      previewWrap,
      actions,
    );
    tipEditor.classList.remove("hidden");
    startTipPreviewLoop(canvas);
  }

  function openTipEditor(row?: AdminChallengeLobbyTip) {
    editingTipId = row?.id ?? null;
    tipDraft = {
      text: row?.text ?? "Stay here a little longer…",
      posX: row?.posX ?? 0.5,
      posY: row?.posY ?? -0.35,
      squareState:
        row?.squareState === "blue" || row?.squareState === "red" || row?.squareState === "both"
          ? row.squareState
          : "both",
      holdSeconds: row?.holdSeconds ?? 5,
      durationSeconds: row?.durationSeconds ?? 4,
      pauseCountdown: !!row?.pauseCountdown,
      hideCountdown: !!row?.hideCountdown,
      active: row ? !!row.active : true,
    };
    buildTipEditor();
  }

  function closeTipEditor() {
    editingTipId = null;
    stopTipPreviewLoop();
    tipEditor.classList.add("hidden");
    tipEditor.replaceChildren();
  }

  function renderTipList() {
    tipListWrap.replaceChildren();
    if (!tipRows.length) {
      const p = document.createElement("p");
      p.className = "accessHint";
      p.textContent = "No idle texts yet.";
      tipListWrap.appendChild(p);
      return;
    }
    const table = document.createElement("table");
    table.className = "leaderboardTable";
    table.innerHTML =
      "<thead><tr><th>Text</th><th>When</th><th>Hold</th><th>Duration</th><th>Pause clock</th><th>Hide clock</th><th>Active</th><th></th></tr></thead>";
    const tbody = document.createElement("tbody");
    for (const t of tipRows) {
      const tr = document.createElement("tr");
      const short = t.text.length > 42 ? `${t.text.slice(0, 40)}…` : t.text;
      tr.innerHTML = `<td>${escapeHtml(short)}</td><td>${escapeHtml(String(t.squareState))}</td><td>${t.holdSeconds}s</td><td>${t.durationSeconds}s</td><td>${t.pauseCountdown ? "Yes" : "No"}</td><td>${t.hideCountdown ? "Yes" : "No"}</td><td>${t.active ? "Yes" : "No"}</td>`;
      const td = document.createElement("td");
      const edit = document.createElement("button");
      edit.type = "button";
      edit.className = "btn";
      edit.textContent = "Edit";
      edit.onclick = () => openTipEditor(t);
      const del = document.createElement("button");
      del.type = "button";
      del.className = "btn";
      del.textContent = "Delete";
      del.onclick = async () => {
        if (!confirm("Delete this idle text?")) return;
        try {
          await deleteAdminChallengeLobbyTip(t.id);
          if (editingTipId === t.id) closeTipEditor();
          await refresh();
        } catch (e) {
          status.textContent = e instanceof Error ? e.message : "Delete failed";
        }
      };
      td.append(edit, del);
      tr.appendChild(td);
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    tipListWrap.appendChild(table);
  }

  addTipBtn.onclick = () => openTipEditor();

  addBtn.onclick = () => void openEditor();
  refreshBtn.onclick = () => void refresh();

  type ResetRecipient = { id: string; displayName: string | null };
  let resetScope: "all" | "users" = "all";
  let resetRecipients: ResetRecipient[] = [];
  let resetSearchTimer = 0;

  function closeResetPanel() {
    window.clearTimeout(resetSearchTimer);
    resetPanel.classList.add("hidden");
    resetPanel.replaceChildren();
    resetDailyBtn.disabled = false;
  }

  function openResetPanel() {
    resetScope = "all";
    resetRecipients = [];
    resetPanel.replaceChildren();

    const title = document.createElement("h4");
    title.className = "adminPlayerStatsSubheading";
    title.textContent = "Reset Challenge";

    const hint = document.createElement("p");
    hint.className = "accessHint";
    hint.textContent =
      "Clear today's Challenge attempt so players can try again until their next local midnight.";

    const scopeRow = document.createElement("div");
    scopeRow.className = "formRow adminChallengeResetScopeRow";

    const allLabel = document.createElement("label");
    allLabel.className = "formLabel adminChallengeResetOption";
    const allRadio = document.createElement("input");
    allRadio.type = "radio";
    allRadio.name = "adminChallengeResetScope";
    allRadio.value = "all";
    allRadio.checked = true;
    allLabel.append(allRadio, document.createTextNode(" All players"));

    const usersLabel = document.createElement("label");
    usersLabel.className = "formLabel adminChallengeResetOption";
    const usersRadio = document.createElement("input");
    usersRadio.type = "radio";
    usersRadio.name = "adminChallengeResetScope";
    usersRadio.value = "users";
    usersLabel.append(usersRadio, document.createTextNode(" Specific users"));

    scopeRow.append(allLabel, usersLabel);

    const usersPanel = document.createElement("div");
    usersPanel.className = "adminChallengeResetUsersPanel hidden";

    const usersHint = document.createElement("p");
    usersHint.className = "accessHint";
    usersHint.textContent = "Search and add one or more users.";

    const searchInput = document.createElement("input");
    searchInput.className = "formInput";
    searchInput.type = "search";
    searchInput.placeholder = "Search users (min 2 characters)";
    searchInput.setAttribute("aria-label", "Search users to reset");

    const searchResults = document.createElement("div");
    searchResults.className = "adminShopSendResults";

    const chips = document.createElement("div");
    chips.className = "adminShopSendChips";

    const renderChips = () => {
      chips.replaceChildren();
      if (!resetRecipients.length) {
        const empty = document.createElement("span");
        empty.className = "accessHint";
        empty.textContent = "No users selected.";
        chips.appendChild(empty);
        return;
      }
      for (const u of resetRecipients) {
        const chip = document.createElement("button");
        chip.type = "button";
        chip.className = "btn adminShopSendChip";
        chip.textContent = `${u.displayName || u.id} ×`;
        chip.title = "Remove";
        chip.onclick = () => {
          resetRecipients = resetRecipients.filter((r) => r.id !== u.id);
          renderChips();
          void runSearch();
        };
        chips.appendChild(chip);
      }
    };

    const runSearch = async () => {
      const q = searchInput.value.trim();
      searchResults.replaceChildren();
      if (q.length < 2) return;
      try {
        const users = await searchUsersByName(q);
        for (const u of users) {
          if (resetRecipients.some((r) => r.id === u.id)) continue;
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "btn";
          btn.textContent = u.displayName || u.id;
          btn.onclick = () => {
            resetRecipients.push(u);
            renderChips();
            void runSearch();
          };
          searchResults.appendChild(btn);
        }
        if (!searchResults.childElementCount) {
          const none = document.createElement("span");
          none.className = "accessHint";
          none.textContent = "No users found.";
          searchResults.appendChild(none);
        }
      } catch {
        const fail = document.createElement("span");
        fail.className = "accessHint";
        fail.textContent = "Search failed.";
        searchResults.appendChild(fail);
      }
    };

    searchInput.oninput = () => {
      window.clearTimeout(resetSearchTimer);
      resetSearchTimer = window.setTimeout(() => void runSearch(), 250);
    };

    usersPanel.append(usersHint, searchInput, searchResults, chips);
    renderChips();

    const syncScopeUi = () => {
      resetScope = usersRadio.checked ? "users" : "all";
      usersPanel.classList.toggle("hidden", resetScope !== "users");
    };
    allRadio.onchange = syncScopeUi;
    usersRadio.onchange = syncScopeUi;
    syncScopeUi();

    const confirmBtn = document.createElement("button");
    confirmBtn.type = "button";
    confirmBtn.className = "btn green";
    confirmBtn.textContent = "Reset";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    const actions = document.createElement("div");
    actions.className = "formRow";
    actions.append(confirmBtn, cancelBtn);

    cancelBtn.onclick = () => closeResetPanel();
    confirmBtn.onclick = async () => {
      if (resetScope === "users" && !resetRecipients.length) {
        status.textContent = "Add at least one user, or choose All players.";
        return;
      }
      const confirmMsg =
        resetScope === "all"
          ? "Reset Challenge for ALL players? Everyone will be able to attempt again until their next local midnight."
          : `Reset Challenge for ${resetRecipients.length} selected user${
              resetRecipients.length === 1 ? "" : "s"
            }?`;
      if (!confirm(confirmMsg)) return;

      confirmBtn.disabled = true;
      cancelBtn.disabled = true;
      resetDailyBtn.disabled = true;
      status.textContent =
        resetScope === "all"
          ? "Resetting Challenge for all players…"
          : `Resetting Challenge for ${resetRecipients.length} user${
              resetRecipients.length === 1 ? "" : "s"
            }…`;
      try {
        const result = await resetAdminDailyChallenges({
          scope: resetScope,
          userIds: resetRecipients.map((u) => u.id),
        });
        closeResetPanel();
        if (result.scope === "users") {
          status.textContent = `Challenge reset for selected users: cleared ${result.cleared} of ${result.scanned} matched accounts (${result.requested} requested).`;
        } else {
          status.textContent = `Challenge reset for all players: cleared ${result.cleared} of ${result.scanned} accounts.`;
        }
      } catch (e) {
        status.textContent = e instanceof Error ? e.message : "Reset failed";
        confirmBtn.disabled = false;
        cancelBtn.disabled = false;
        resetDailyBtn.disabled = false;
      }
    };

    resetPanel.append(title, hint, scopeRow, usersPanel, actions);
    resetPanel.classList.remove("hidden");
    resetDailyBtn.disabled = true;
  }

  resetDailyBtn.onclick = () => openResetPanel();

  saveRewardsBtn.onclick = async () => {
    const sum = rewardRows.reduce((s, r) => s + r.probabilityBasisPoints, 0);
    if (sum !== 10000) {
      status.textContent = `Reward probabilities must sum to 100% (currently ${formatPercent(sum)}).`;
      return;
    }
    try {
      rewardRows = await saveAdminChallengeRewardWeights(
        rewardRows.map((r) => ({
          amountDots: r.amountDots,
          probabilityPercent: r.probabilityPercent,
        })),
      );
      renderRewards();
      status.textContent = "Reward probabilities saved.";
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Save failed";
    }
  };

  void refresh();

  return {
    refresh,
    destroy: () => {
      stopTipPreviewLoop();
    },
  };
}
